import React from 'react';
import PropTypes from 'prop-types';
import { createFragmentContainer, graphql } from 'react-relay';
import Helmet from 'react-helmet';
import get from 'lodash.get';

import { ServerVarsContextConsumer } from '../../global/ServerVarsContext/ServerVarsContext';
import { addTrailingSlash } from '../../utils/uriUtils';

const MAX_IMAGE_COUNT = 3;
const IMAGE_PATH = 'node.item.photos[0].versions[0].webPath';

function SharedHeadMetaTagsComponent({ metadata, items, localeOrigin, clientConf, imageFallback }) {
    const { title, description, canonical, disableIndexing } = metadata;
    const canonicalUrl = canonical ? addTrailingSlash(`${localeOrigin}/${canonical}`) : '';

    const metaTagsArray = [
        {
            name: 'description',
            content: description || '',
        },
        {
            name: 'og:title',
            content: title,
        },
        {
            name: 'og:type',
            content: 'website',
        },
        {
            name: 'og:site_name',
            content: '1stdibs.com',
        },
        {
            name: 'fb:admins',
            content: clientConf.facebookAdmin,
        },
        {
            name: 'fb:app_id',
            content: clientConf.facebookAppId,
        },
        {
            name: 'fb:page_id',
            content: clientConf.facebookPageId,
        },
        {
            name: 'twitter:card',
            content: 'summary',
        },
        {
            name: 'twitter:site',
            content: '@1stdibs',
        },
        {
            name: 'twitter:title',
            content: title,
        },
        {
            name: 'twitter:description',
            content: description || '',
        },
        {
            name: 'twitter:image',
            content: get(items, `[0].${IMAGE_PATH}`) || imageFallback || '',
        },
    ];

    // itemSearchQuery has indexation logic on canonical field because of that we need to do a check here
    if (canonicalUrl) {
        metaTagsArray.push({
            name: 'og:url',
            content: canonicalUrl,
        });
        metaTagsArray.push({
            name: 'twitter:url',
            content: canonicalUrl,
        });
    }
    if (items.length) {
        items.slice(0, MAX_IMAGE_COUNT).map(item => {
            metaTagsArray.push({
                name: 'og:image',
                content: get(item, IMAGE_PATH) || '',
            });
        });
    } else if (imageFallback) {
        metaTagsArray.push({ name: 'og:image', content: imageFallback });
    }

    if (disableIndexing) {
        metaTagsArray.push({
            name: 'robots',
            content: 'noindex, follow',
        });
    }

    return <Helmet meta={metaTagsArray} />;
}

SharedHeadMetaTagsComponent.propTypes = {
    metadata: PropTypes.object.isRequired,
    items: PropTypes.array.isRequired,
    clientConf: PropTypes.object.isRequired,
    localeOrigin: PropTypes.string.isRequired,
    imageFallback: PropTypes.string,
};

function SharedHeadMetaTagsServerVarsContextComponent(props) {
    return (
        <ServerVarsContextConsumer>
            {({ clientConf, localeOrigin }) => (
                <SharedHeadMetaTagsComponent
                    {...props}
                    clientConf={clientConf}
                    localeOrigin={localeOrigin}
                />
            )}
        </ServerVarsContextConsumer>
    );
}

/*
 * We need items for this component. Since Querys created using dibs-graphql helper `createConnection`
 * has `edges` field which has a different name for every query we need to unmask this data in parent
 * component and pass items as prop.
 */
// eslint-disable-next-line
const fragment = {
    node: graphql`
        fragment SharedHeadMetaTags_node on Item {
            photos(limit: 1) {
                versions {
                    webPath
                }
            }
        }
    `,
};

export const SharedHeadMetaTags = createFragmentContainer(
    SharedHeadMetaTagsServerVarsContextComponent,
    {
        metadata: graphql`
            fragment SharedHeadMetaTags_metadata on MetaData {
                title
                description
                canonical
                disableIndexing
            }
        `,
    }
);
